import React, { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Search,
  Plus,
  ArrowRight,
  Link as LinkIcon,
  RefreshCw,
  Mail,
  FileText,
  Check,
} from 'lucide-react';
import { TemporaryCaseItem, TicketItem, BranchItem } from '../types/index.ts';

interface TemporaryCasesPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const TemporaryCasesPage: React.FC<TemporaryCasesPageProps> = ({ onNavigate }) => {
  const [cases, setCases] = useState<TemporaryCaseItem[]>([]);
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  // Match Modal
  const [matchingCase, setMatchingCase] = useState<TemporaryCaseItem | null>(null);
  const [targetTicketNumber, setTargetTicketNumber] = useState('');
  const [selectedExistingTicketId, setSelectedExistingTicketId] = useState('');
  const [createIfMissing, setCreateIfMissing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // New Case Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCaseForm, setNewCaseForm] = useState({
    branchCode: 'UBL-0142',
    branchName: 'UBL Clifton Block 5 Branch',
    issueSubject: '',
    workType: 'Paint / Tile / Seepage / Front Elevation',
    senderEmail: 'operations.branch@ubl.com.pk',
    senderName: 'Branch Manager',
    notes: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [casesRes, ticketsRes, branchesRes] = await Promise.all([
        fetch('/api/temporary-cases').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/branches').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(casesRes)) setCases(casesRes);
      if (Array.isArray(ticketsRes)) setTickets(ticketsRes);
      if (Array.isArray(branchesRes)) setBranches(branchesRes);
    } catch (err: any) {
      console.warn('Temporary cases load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleMatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matchingCase) return;

    try {
      setIsSubmitting(true);
      const ticketNum = targetTicketNumber.trim();
      const res = await fetch('/api/temporary-cases/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tempCaseId: matchingCase.id,
          ticketNumber: ticketNum || undefined,
          ticketId: selectedExistingTicketId ? Number(selectedExistingTicketId) : undefined,
          createIfMissing,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Temporary Case matched to Ticket #${data.ticket?.ticketNumber || ticketNum}`);
        setMatchingCase(null);
        setTargetTicketNumber('');
        setSelectedExistingTicketId('');
        await fetchData();
      } else {
        setToastMessage(data.error || 'Failed to match case');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Matching error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleCreateCase = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const res = await fetch('/api/temporary-cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCaseForm),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Temporary case created: ${data.tempCaseNumber}`);
        setShowCreateModal(false);
        setNewCaseForm({
          branchCode: 'UBL-0142',
          branchName: 'UBL Clifton Block 5 Branch',
          issueSubject: '',
          workType: 'Paint / Tile / Seepage / Front Elevation',
          senderEmail: 'operations.branch@ubl.com.pk',
          senderName: 'Branch Manager',
          notes: '',
        });
        await fetchData();
      } else {
        setToastMessage(data.error || 'Failed to create case');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Creation error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const pendingCases = cases.filter((c) => c.status === 'PENDING_TICKET_NUMBER');
  const matchedCases = cases.filter((c) => c.status === 'MATCHED_TO_TICKET');

  const filteredCases = cases.filter((c) => {
    const matchesStatus = filterStatus === 'ALL' || c.status === filterStatus;
    const matchesSearch =
      c.tempCaseNumber.toLowerCase().includes(search.toLowerCase()) ||
      c.branchName.toLowerCase().includes(search.toLowerCase()) ||
      c.branchCode.toLowerCase().includes(search.toLowerCase()) ||
      c.issueSubject.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Temporary Cases &amp; Ticket Matching
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
              Pre-Ticket Pipeline
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Track inquiries received from branches before an official UBL Central Ticket Number is issued. Avoids duplicates and merges seamlessly once approved.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-blue-200" />
            New Temporary Case
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">Awaiting Ticket Number</span>
            <Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-2xl font-bold text-amber-600 mt-2">{pendingCases.length}</div>
          <div className="text-xs text-slate-400 mt-1">Inquiries pending UBL helpdesk ticket ID</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">Matched to Official Ticket</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-2">{matchedCases.length}</div>
          <div className="text-xs text-slate-400 mt-1">Merged without generating duplicates</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">Total Recorded Cases</span>
            <Building2 className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2">{cases.length}</div>
          <div className="text-xs text-slate-400 mt-1">Total incoming branch inquiries</div>
        </div>
      </div>

      {/* Table & Filtering */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('ALL')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === 'ALL'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              All Cases ({cases.length})
            </button>
            <button
              onClick={() => setFilterStatus('PENDING_TICKET_NUMBER')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === 'PENDING_TICKET_NUMBER'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              Pending Ticket No ({pendingCases.length})
            </button>
            <button
              onClick={() => setFilterStatus('MATCHED_TO_TICKET')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === 'MATCHED_TO_TICKET'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              Matched ({matchedCases.length})
            </button>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search case #, branch, subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-64"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                <th className="py-3 px-4">Case #</th>
                <th className="py-3 px-4">Branch Details</th>
                <th className="py-3 px-4">Subject &amp; Work Type</th>
                <th className="py-3 px-4">Sender / Source</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Matched Ticket</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {filteredCases.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No temporary cases found matching query.
                  </td>
                </tr>
              ) : (
                filteredCases.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-slate-900">{c.tempCaseNumber}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{c.branchName}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{c.branchCode}</div>
                    </td>
                    <td className="py-3 px-4 max-w-xs">
                      <div className="font-medium text-slate-800 line-clamp-1">{c.issueSubject}</div>
                      <div className="text-[11px] text-slate-500">{c.workType}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-800">{c.senderName || 'Branch'}</div>
                      <div className="text-[11px] text-slate-400">{c.senderEmail}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                          c.status === 'MATCHED_TO_TICKET'
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : 'bg-amber-50 text-amber-800 border border-amber-200'
                        }`}
                      >
                        {c.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {c.matchedTicketNumber ? (
                        <button
                          onClick={() => onNavigate('tickets')}
                          className="font-mono font-bold text-blue-700 hover:underline flex items-center gap-1"
                        >
                          {c.matchedTicketNumber} &rarr;
                        </button>
                      ) : (
                        <span className="text-slate-400 italic text-[11px]">Unlinked</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {c.status === 'PENDING_TICKET_NUMBER' ? (
                        <button
                          onClick={() => {
                            setMatchingCase(c);
                            setTargetTicketNumber('');
                            setSelectedExistingTicketId('');
                          }}
                          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors inline-flex items-center gap-1"
                        >
                          <LinkIcon className="w-3 h-3" />
                          Match Ticket
                        </button>
                      ) : (
                        <span className="text-emerald-600 font-medium text-[11px] flex items-center justify-end gap-1">
                          <Check className="w-3.5 h-3.5" />
                          Linked
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Match Ticket Modal */}
      {matchingCase && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">
                  Match Temporary Case: {matchingCase.tempCaseNumber}
                </h3>
              </div>
              <button
                onClick={() => setMatchingCase(null)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleMatchSubmit} className="p-5 space-y-4 text-xs">
              <div className="p-3 bg-blue-50 rounded-lg text-blue-900 space-y-1">
                <div className="font-bold">{matchingCase.branchName} ({matchingCase.branchCode})</div>
                <div className="text-[11px] text-blue-700">{matchingCase.issueSubject}</div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">
                  Enter UBL Ticket Number (e.g. 126058 or TKT-2026-004)
                </label>
                <input
                  type="text"
                  placeholder="Enter ticket number received from UBL"
                  value={targetTicketNumber}
                  onChange={(e) => setTargetTicketNumber(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="shrink mx-2 text-[10px] text-slate-400 uppercase">OR SELECT EXISTING TICKET</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Select from ERP Tickets</label>
                <select
                  value={selectedExistingTicketId}
                  onChange={(e) => setSelectedExistingTicketId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">-- Choose existing ticket --</option>
                  {tickets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.ticketNumber} — {t.branchName} ({t.status})
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-slate-700 pt-1">
                <input
                  type="checkbox"
                  checked={createIfMissing}
                  onChange={(e) => setCreateIfMissing(e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px]">
                  Automatically create official Ticket in ERP if ticket number does not already exist
                </span>
              </label>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setMatchingCase(null)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || (!targetTicketNumber && !selectedExistingTicketId)}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" />
                  {isSubmitting ? 'Matching...' : 'Confirm Match'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Temporary Case Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Record New Temporary Case</h3>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateCase} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Branch</label>
                <select
                  required
                  value={newCaseForm.branchCode}
                  onChange={(e) => {
                    const found = branches.find((b) => b.branchCode === e.target.value);
                    setNewCaseForm({
                      ...newCaseForm,
                      branchCode: e.target.value,
                      branchName: found ? found.branchName : `Branch ${e.target.value}`,
                    });
                  }}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.branchCode}>
                      {b.branchCode} — {b.branchName} ({b.city})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Issue / Subject</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ATM vestibule AC leaking or cash counter repair"
                  value={newCaseForm.issueSubject}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, issueSubject: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Work Type</label>
                <select
                  value={newCaseForm.workType}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, workType: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="AC">AC (Air Conditioning)</option>
                  <option value="Blinds">Blinds</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Genset">Genset</option>
                  <option value="Glass / Door / Wood Work">Glass / Door / Wood Work</option>
                  <option value="Grill / Window / Genset / Other">Grill / Window / Genset / Other</option>
                  <option value="Paint / Tile / Seepage / Front Elevation">Paint / Tile / Seepage / Front Elevation</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Ramps">Ramps</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Sender Name</label>
                  <input
                    type="text"
                    value={newCaseForm.senderName}
                    onChange={(e) => setNewCaseForm({ ...newCaseForm, senderName: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Sender Email</label>
                  <input
                    type="email"
                    value={newCaseForm.senderEmail}
                    onChange={(e) => setNewCaseForm({ ...newCaseForm, senderEmail: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Internal Notes</label>
                <textarea
                  rows={2}
                  value={newCaseForm.notes}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, notes: e.target.value })}
                  placeholder="Notes from initial phone call or WhatsApp message..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save Temporary Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
